import { updateContract } from "@/requests/contract/update";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Contract, ContractState } from "@/types/contract";
import { ReactQueryKeys } from "@/constants/common";
import { updateRequest } from "@/requests/request/update";
import { useIsContractRequest } from "@/hooks/useIsContractRequest";
import { handleMutationError } from "@/utils/error";

interface UseUpdateMutation {
  uuid: string;
  form?: { resetDirty: () => void };
  finishContract?: boolean;
  finishRequest?: boolean;
  optimistic?: boolean;
  fromOldApplication?: boolean;
  findDistributor?: boolean;
  createInSap?: boolean;
  onSuccess?: (updatedContract: Contract) => void;
  onContinue?: (updatedContract: Contract) => void;
  onError?: (error: Error) => void;
}

export const useUpdateMutation = ({
  uuid,
  form,
  finishContract = false,
  finishRequest = false,
  optimistic = false,
  fromOldApplication = false, // fromOldApplication is used by BE for calculating recommended frequency
  findDistributor = false, // findDistributor is used by BE for filling distributor on supply point
  createInSap = false,
  onSuccess,
  onContinue,
  onError,
}: UseUpdateMutation) => {
  const queryClient = useQueryClient();
  const isRequest = useIsContractRequest();
  const queryKey = ReactQueryKeys.getContract(uuid, isRequest);

  return useMutation({
    mutationKey: ReactQueryKeys.mutateContract(),
    mutationFn: (updatedContract: Contract) => {
      return isRequest
        ? updateRequest(uuid, updatedContract, { finishRequest })
        : updateContract(uuid, updatedContract, { finishContract, fromOldApplication, findDistributor, createInSap });
    },
    onMutate: async (updatedContract: Contract) => {
      if (optimistic) {
        form?.resetDirty();
        await queryClient.cancelQueries({ queryKey });
        const contractSnapshot = queryClient.getQueryData<ContractState>(queryKey);
        queryClient.setQueryData(queryKey, (oldData: ContractState) => ({
          ...oldData,
          contract: updatedContract,
        }));
        onContinue?.(updatedContract);
        return { contractSnapshot, optimistic };
      } else {
        return {};
      }
    },
    onSuccess: (updatedContract: Contract, _, context) => {
      onSuccess?.(updatedContract);
      queryClient.setQueryData(queryKey, (oldData: ContractState) => ({
        ...oldData,
        contract: updatedContract,
      }));
      if (!context.optimistic) {
        form?.resetDirty();
        onContinue?.(updatedContract);
      }
    },
    onError: (e, updatedContract, context) => {
      handleMutationError();
      onError?.(e);
      if (optimistic && context) {
        queryClient.setQueryData(queryKey, context.contractSnapshot);
      }
    },
  });
};
